import type { FC } from 'react';

import { useCallback, useEffect, useState } from 'react';

import { appConfig } from '../../../../appConfig';
import { useAppDispatch, useAppSelector } from '../../hooks/useThunkDispatch';
import { useTimeExpirationWithAsyncEnsure } from '../../hooks/useTimeExpiration';
import { cartSelectors } from '../../selectors/cartSelectors';
import { debugSelectors } from '../../selectors/debugSelectors';
import { modalActions } from '../../store/slices/modal/modalSlice';
import { cartThunks } from '../../thunks/cartThunks';
import EditOrderExpiredDialog from './EditOrderExpiredDialog';
import EditOrderSoonExpireDialog, { EXPIRE_IN_MINUTES } from './EditOrderSoonExpireDialog';

const getMilisecondsToExpire = (date: Date) => {
    const milisecondsToExpire = date && date.getTime() - new Date().getTime();

    return milisecondsToExpire;
};

const useEditOrderSoonExpireTimer = () => {
    const dispatch = useAppDispatch();
    const [showDialog, setShowDialog] = useState(false);

    const onExpireCallback = useCallback(() => {
        setShowDialog(true);
    }, []);

    const editOrderExpirationTime = useAppSelector(cartSelectors.editOrderExpirationTime);
    const { mockEditOrderIsExpiringSoon } = useAppSelector(debugSelectors.selectDebug);

    const modifiedTime = editOrderExpirationTime && new Date(editOrderExpirationTime);
    if (modifiedTime) {
        modifiedTime.setMinutes(editOrderExpirationTime.getMinutes() - EXPIRE_IN_MINUTES);
    }

    const milisecondsToExpire = modifiedTime && getMilisecondsToExpire(modifiedTime);

    const [stopTimer, setStopTimer] = useState(false);

    // mock display of the "soon expire" dialog
    useEffect(() => {
        if (mockEditOrderIsExpiringSoon) {
            setShowDialog(true);
        }
    }, [mockEditOrderIsExpiringSoon]);

    useEffect(() => {
        if (milisecondsToExpire) {
            // when "now" is greater than "expire time", stop the timer for "soonExpire", it's time to start "expired" timer
            setStopTimer(milisecondsToExpire < 0);
        }
    }, [milisecondsToExpire]);

    const asyncEnsureCall = useCallback(
        async () => dispatch(cartThunks.getCartSilent()),
        [dispatch],
    );

    useTimeExpirationWithAsyncEnsure(
        milisecondsToExpire,
        asyncEnsureCall,
        onExpireCallback,
        stopTimer,
    );

    return {
        showDialog,
        stoppedTimer: stopTimer,
        closeDialog: useCallback(() => setShowDialog(false), []),
    };
};

const useEditOrderExpiredTimer = (previousCounterStopped: boolean) => {
    const dispatch = useAppDispatch();
    const [showDialog, setShowDialog] = useState(false);
    const [stopTimer, setStopTimer] = useState(false);
    const { mockEditOrderIsExpired } = useAppSelector(debugSelectors.selectDebug);

    useEffect(() => {
        // "expired" counter can only start when "soonExpire" timer ended
        setStopTimer(!previousCounterStopped);
    }, [previousCounterStopped]);

    // mock display of the "expired" dialog
    useEffect(() => {
        if (mockEditOrderIsExpired) {
            setShowDialog(true);
        }
    }, [mockEditOrderIsExpired]);

    const onExpireCallback = useCallback(() => {
        setShowDialog(true);
    }, []);

    const editOrderExpirationTime = useAppSelector(cartSelectors.editOrderExpirationTime);

    const milisecondsToExpire =
        editOrderExpirationTime && getMilisecondsToExpire(editOrderExpirationTime); // 60 minutes +   5sec for mock

    const asyncEnsureCall = useCallback(
        async () => dispatch(cartThunks.getCartSilent()),
        [dispatch],
    );

    useTimeExpirationWithAsyncEnsure(
        milisecondsToExpire,
        asyncEnsureCall,
        onExpireCallback,
        stopTimer,
    );

    return {
        showDialog,
        closeDialog: useCallback(() => setShowDialog(false), []),
    };
};

const EditOrderExpirationDialogs: FC<React.PropsWithChildren<unknown>> = () => {
    const dispatch = useAppDispatch();
    const isEditOrderMode = useAppSelector(cartSelectors.isInEditOrder);
    const editOrderExpirationTime = useAppSelector(cartSelectors.editOrderExpirationTime);

    const {
        showDialog: showSoonExpireDialog,
        stoppedTimer: soonExpireTimerStopped,
        closeDialog: closeSoonExpireDialog,
    } = useEditOrderSoonExpireTimer();

    const { showDialog: showExpiredDialog, closeDialog: closeExpiredDialog } =
        useEditOrderExpiredTimer(soonExpireTimerStopped);

    useEffect(() => {
        // only one dialog can be opened at a time
        if (showExpiredDialog) {
            closeSoonExpireDialog();
        }
    }, [showExpiredDialog, closeSoonExpireDialog]);

    const closeModals = useCallback(() => dispatch(modalActions.close()), [dispatch]);
    useEffect(() => {
        if (showSoonExpireDialog || showExpiredDialog) {
            closeModals();
        }
    }, [showSoonExpireDialog, showExpiredDialog, closeModals]);

    return (
        <>
            {isEditOrderMode && !!editOrderExpirationTime && (
                <EditOrderSoonExpireDialog
                    showDialog={showSoonExpireDialog}
                    editOrderExpirationTime={editOrderExpirationTime}
                    handleConfirm={() => {
                        closeSoonExpireDialog();
                        window.location.href = appConfig.coopSettings.ecommerce.checkout.url;
                    }}
                    close={closeSoonExpireDialog}
                />
            )}
            <EditOrderExpiredDialog
                showDialog={showExpiredDialog}
                handleConfirm={() => {
                    closeExpiredDialog();
                    window.location.href = appConfig.coopSettings.myCoop.lastOrdersUrl;
                }}
                close={closeExpiredDialog}
            />
        </>
    );
};

export default EditOrderExpirationDialogs;
